import { Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { PartnerSearchType } from '@evimo/types/enums/partner-search-type';
import { PartnerHeroCard } from '~/app/landing/sla/shared/components/PartnerHeroCard';
import { useEffectiveOrganizationName } from '~/app/landing/sla/shared/hooks/useEffectiveOrganizationName';
import { LandlordSlaReviewSummary } from '~/app/landing/sla/shared/components/SlaReviewSummary';
import { PartnerOrgAutocomplete } from '~/app/landing/sla/shared/PartnerOrgAutocomplete';
import type { PartnerOrgSelection } from '~/app/landing/sla/shared/types';
import type { LandlordPartnerFormValues } from '@/app/landing/sla/landlords/LandlordPartnerModal/types';
import { LandlordPartnerContractFields } from '@/app/landing/sla/landlords/LandlordPartnerModal/components/LandlordPartnerContractFields';

type Props = {
  organizationId: string;
  isEdit: boolean;
  step: number;
  selectedPartner: PartnerOrgSelection | null;
  onPartnerChange: (partner: PartnerOrgSelection | null) => void;
  contractSnapshot: LandlordPartnerFormValues | null;
};

export function LandlordPartnerModalBody({
  organizationId,
  isEdit,
  step,
  selectedPartner,
  onPartnerChange,
  contractSnapshot,
}: Props) {
  const { t } = useTranslation('sla');
  const operatorOrganizationName = useEffectiveOrganizationName(organizationId);

  return (
    <>
      {!isEdit && step === 0 && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            {t('stepper.partnerHint')}
          </Typography.Text>
          <PartnerOrgAutocomplete
            organizationId={organizationId}
            searchType={PartnerSearchType.LANDLORD}
            placeholder={t('landlords.partnerSearchPlaceholder')}
            partner={selectedPartner}
            onPartnerChange={onPartnerChange}
          />
        </Space>
      )}

      {!isEdit && step === 1 && (
        <PartnerHeroCard
          partner={selectedPartner}
          variant="landlord"
          operatorOrganizationName={operatorOrganizationName}
        />
      )}

      {(isEdit || step >= 2) && step !== 3 && <LandlordPartnerContractFields />}

      {!isEdit && step === 3 && contractSnapshot && (
        <LandlordSlaReviewSummary
          partnerName={selectedPartner?.name ?? '—'}
          uptimeTargetPct={contractSnapshot.uptimeTargetPct}
          responseDcDays={contractSnapshot.responseDcDays}
          responseAcDays={contractSnapshot.responseAcDays}
          penaltyPerDayAmount={contractSnapshot.penaltyPerDayAmount}
          penaltyCurrency={contractSnapshot.penaltyCurrency}
          paymentModel={contractSnapshot.paymentModel}
          status={contractSnapshot.status}
          validFrom={contractSnapshot.validFrom}
          validTo={contractSnapshot.validTo ?? null}
        />
      )}
    </>
  );
}
